import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import TooltipTsoPq from '@components/Tooltip/TooltipTsoPq';
import { Box, Button, ButtonProps, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { buttonProps } from './ButtonConfig';

type StyledButtonProps = ButtonProps & {
  buttonType: ButtonType;
  sizeVariant?: SizeVariant;
  iconLeft?: JSX.Element;
  iconRight?: JSX.Element;
};

type ButtonTsoPqProps = StyledButtonProps & {
  tooltip?: string;
  tooltipVariant?: 'default' | 'legend';
  textContent?: string;
};

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => !['sizeVariant', 'buttonType', 'iconLeft', 'iconRight'].includes(prop as string),
})<StyledButtonProps>(({ sizeVariant, buttonType, iconLeft, iconRight }) => {
  const hasIcon = iconLeft ?? iconRight;
  const iconType = iconLeft ? 'iconLeft' : 'iconRight';
  const toIconOrNotToIcon = hasIcon ? iconType : 'noIcon';
  const buttonSize = sizeVariant ?? 'm';
  const borderColor = buttonProps.border[buttonType] ?? undefined;

  return {
    height: buttonProps.classicButton.size[buttonSize],
    padding: buttonProps.classicButton.padding[buttonSize][toIconOrNotToIcon],
    gap: tsoPqTheme.spacingValues.buttonSpacing.spacing_button_large_button_lg_gap,
    background: buttonProps.background[buttonType],
    color: buttonProps.color[buttonType],
    textTransform: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: borderColor && `1px solid ${borderColor.normal}`,
    borderRadius: tsoPqTheme.shape.border_radius_tso_border_radius_4,
    cursor: ['disabledFilled', 'disabledOutline'].includes(buttonType) ? 'default' : 'pointer',

    '&:hover': {
      background: buttonProps.hover[buttonType],
      border: borderColor && `1px solid ${borderColor.hover}`,
    },
    '&:active': {
      background: buttonProps.active[buttonType],
      border: borderColor && `1px solid ${borderColor.hover}`,
    },
    '&.Mui-disabled': {
      backgroundColor: tsoPqTheme.palette.customTextColor.color_neutral_font_ft_ntrl_lt,
      color: tsoPqTheme.palette.customTextColor.color_font_ft_nrtrl_disabled,
      cursor: 'not-allowed',
    },
  };
});

const IconWrapper = styled(Box)<{ margin?: string }>(({ margin }) => ({
  display: 'flex',
  alignItems: 'center',
  margin,
}));

const StyledTypography = styled(Typography)({
  fontFamily: tsoPqTheme.typography.openSans,
  lineHeight: '1.125rem',
  fontWeight: 400,
  fontStyle: 'normal',
});

const StyledIconWrapper = styled(IconWrapper)(({ sizeVariant = 'm' }: { sizeVariant: SizeVariant }) => {
  return {
    '& .MuiSvgIcon-root': {
      fontSize: buttonProps.classicButton.fontSize[sizeVariant],
    },
  };
});

const ButtonTsoPq = ({
  textContent,
  iconLeft,
  iconRight,
  sizeVariant = 'm',
  buttonType,
  tooltip,
  tooltipVariant = 'default',
  ...props
}: ButtonTsoPqProps) => {
  const button = (
    <StyledButton
      disableRipple
      sizeVariant={sizeVariant}
      buttonType={buttonType}
      iconLeft={iconLeft}
      iconRight={iconRight}
      {...props}
    >
      {iconLeft && <StyledIconWrapper sizeVariant={sizeVariant}>{iconLeft}</StyledIconWrapper>}
      {textContent && (
        <StyledTypography fontSize={buttonProps.classicButton.fontSize[sizeVariant]}>{textContent}</StyledTypography>
      )}
      {iconRight && <StyledIconWrapper sizeVariant={sizeVariant}>{iconRight}</StyledIconWrapper>}
    </StyledButton>
  );

  // This "span" wrapper is needed because we need tooltips on certain "disabled" buttons. Here is the warning in the console
  // when there is no wrapper:
  // "You are providing a disabled `button` child to the Tooltip component.
  // A disabled element does not fire events.
  // Tooltip needs to listen to the child element's events to display the title.
  // Add a simple wrapper element, such as a `span`."
  return tooltip ? (
    <TooltipTsoPq title={tooltip} variant={tooltipVariant}>
      <span>{button}</span>
    </TooltipTsoPq>
  ) : (
    button
  );
};

export default ButtonTsoPq;
